import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type KeyboardEvent, type ReactNode } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { THERAGENIE_LABEL } from "@/features/page_agent/shared/constants/pageAgentLabels";
import type { ReviewDecision, CaseFieldEvidence } from "../../model/caseReview";
import type { FieldProposal } from "../../model/agentProposal";
import { isReviewerChange, REVIEW_DECISIONS } from "../../constants/caseReviewDecisions";
import { MANUAL_ENTRY_ACCEPT_TITLE, MANUAL_ENTRY_FLAG_LABEL, PROPOSAL_ACCEPT_TITLE, PROPOSAL_PILL_TITLE, PROPOSAL_REDO_TITLE, PROPOSAL_REJECT_TITLE, PROPOSAL_UNDO_TITLE, hasProposalVersions, proposalRedoName, proposalUndoName, proposalVersionText } from "../../constants/caseReviewLabels";
import { CaseReviewIcon } from "./CaseReviewIcon";

const CONFIDENCE_LABEL: Record<CaseFieldEvidence["confidence"], string> = { high: "High", medium: "Med", low: "Low" };
const DECISION_LABEL: Record<ReviewDecision, string> = { accepted: "Accepted", corrected: "Edited", rejected: "Rejected" };
const REJECT_AI_VALUE_TITLE = "Reject AI value";
const RESTORE_AI_VALUE_LABEL = "Restore AI value";

/** Beside the confidence pill of a field whose extraction could not be used: the reason is in the reasoning. */
export function ManualEntryFlag() {
  return <span className="cr-manual-entry-flag" role="img" aria-label={MANUAL_ENTRY_FLAG_LABEL} title={MANUAL_ENTRY_FLAG_LABEL}>
    <CaseReviewIcon name="warning" size={13} strokeWidth={2} />
  </span>;
}

/** Small tag beside a value the reviewer changed; the confidence pill keeps showing the AI's confidence. */
export function EditedTag() {
  return <span className="cr-edited-tag" title="Value changed by the reviewer">edited</span>;
}

/** Beside an unsaved change while editing: puts the field back to its saved value and decision. */
export function RevertButton({ label, onRevert }: { label: string; onRevert: () => void }) {
  return <button type="button" className="cr-revert" aria-label={`Revert ${label} to its saved value`} title="Revert to saved value"
    onClick={(event) => { event.stopPropagation(); onRevert(); }}><CaseReviewIcon name="undo" size={12} strokeWidth={2.2} />Revert</button>;
}

/**
 * Beside a pending proposal while editing: steps its field set back one version — an alternative
 * the agent named, an earlier run's proposal, or the values before any proposal — or forward again
 * to a version an Undo stepped away from, and says where the shown version stands. Revert-to-saved
 * stays on `RevertButton`; this is the proposal history.
 */
export function ProposalVersionStepper({ label, proposal }: { label: string; proposal: FieldProposal }) {
  const { version } = proposal;
  return <>
    {hasProposalVersions(version) && <span className="cr-proposal-version" data-proposal-version={version.index}>{proposalVersionText(version)}</span>}
    <button type="button" className="cr-proposal-undo" aria-label={proposalUndoName(label)} title={PROPOSAL_UNDO_TITLE}
      onClick={(event) => { event.stopPropagation(); proposal.undo(); }}><CaseReviewIcon name="undo" size={12} strokeWidth={2.2} />Undo</button>
    {version.canRedo && <button type="button" className="cr-proposal-redo" aria-label={proposalRedoName(label)} title={PROPOSAL_REDO_TITLE}
      onClick={(event) => { event.stopPropagation(); proposal.redo(); }}><CaseReviewIcon name="redo" size={12} strokeWidth={2.2} />Redo</button>}
  </>;
}

interface PillProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  evidence?: CaseFieldEvidence;
  decision?: ReviewDecision;
  open: boolean;
  showConfidence?: boolean;
}

/** The AI-confidence pill; it doubles as the trigger of the reasoning popover. */
export const ConfidencePill = forwardRef<HTMLButtonElement, PillProps>(function ConfidencePill(
  { evidence, decision, open, showConfidence = true, ...buttonProps },
  ref,
) {
  if (!evidence && !decision) return null;
  const shownDecision = decision === REVIEW_DECISIONS.accepted || decision === REVIEW_DECISIONS.rejected ? decision : undefined;
  if (!showConfidence && !shownDecision && evidence) {
    return <button {...buttonProps} ref={ref} type="button" className="cr-manual-entry-flag"
      title={MANUAL_ENTRY_FLAG_LABEL} aria-label={MANUAL_ENTRY_FLAG_LABEL} aria-expanded={open} aria-haspopup="dialog">
      <CaseReviewIcon name="warning" size={13} strokeWidth={2} />
    </button>;
  }
  const text = shownDecision ? DECISION_LABEL[shownDecision] : evidence ? CONFIDENCE_LABEL[evidence.confidence] : DECISION_LABEL[decision!];
  const title = `${shownDecision ? DECISION_LABEL[shownDecision] : `${text} confidence`} · why did AI extract this?`;
  return <button {...buttonProps} ref={ref} type="button" className="cr-pill" data-confidence={showConfidence ? evidence?.confidence : undefined} data-decision={shownDecision} data-open={open}
    title={title} aria-expanded={open} aria-haspopup="dialog">
    {text}{evidence && <CaseReviewIcon name="info" size={11} strokeWidth={2.2} />}
  </button>;
});

interface PopoverProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  label: string; evidence: CaseFieldEvidence; decision?: ReviewDecision; canDecide: boolean; hasSource: boolean;
  /** False when the extraction could not be used as the field's type: Accept shows, disabled. */
  canAccept?: boolean;
  showConfidence?: boolean;
  onSource: () => void; onAccept: () => void; onReject: () => void; onRestoreAiValue: () => void; onClose: () => void;
  /**
   * Every change ever made to this field, when it can be asked for.
   *
   * <p>A slot rather than the entries themselves: the read is lazy and owns its own request state,
   * and this component should not know that asking costs anything. Absent for a field whose stored
   * row the client cannot name — a scalar section's row pk is not in the review document, so those
   * fields show reasoning without history until it is.
   */
  historySlot?: ReactNode;
}

/** Anchored below the pill: what the model extracted and why, with the same decisions the row offers. */
export const ReasoningPopover = forwardRef<HTMLDivElement, PopoverProps>(function ReasoningPopover(
  { label, evidence, decision, canDecide, canAccept = true, showConfidence = true, hasSource, onSource, onAccept, onReject, onRestoreAiValue, onClose, historySlot,
    className, onKeyDown: onContentKeyDown, onClick: onContentClick, ...contentProps },
  ref,
) {
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    onContentKeyDown?.(event);
    if (!event.defaultPrevented && event.key === "Escape") { event.stopPropagation(); onClose(); }
  };
  // Popover.Content uses asChild: its pointer/focus capture handlers must reach this DOM node.
  // Dropping them makes an inside pointer-down dismiss the popover before the button can click.
  return <div {...contentProps} ref={ref} className={`cr-portal cr-popover${className ? ` ${className}` : ""}`}
    role="dialog" aria-label={`AI reasoning for ${label}`} onKeyDown={onKeyDown}
    onClick={(event) => { onContentClick?.(event); event.stopPropagation(); }}>
    <div className="cr-popover-head">
      <span className="cr-popover-spark"><CaseReviewIcon name="spark" size={15} /></span>
      <div className="cr-popover-title">
        <div className="cr-popover-label">{label}</div>
        {showConfidence && <div className="cr-popover-meta"><span className="cr-pill cr-pill-static" data-confidence={evidence.confidence}>{CONFIDENCE_LABEL[evidence.confidence]} confidence</span></div>}
      </div>
      <button type="button" className="cr-popover-close" aria-label="Close" onClick={onClose}><CaseReviewIcon name="x" size={12} strokeWidth={2.2} /></button>
    </div>
    <p className="cr-popover-text">{evidence.reasoning || evidence.quote}</p>
    {historySlot}
    <div className="cr-popover-actions">
      {hasSource && <button type="button" className="cr-popover-source" onClick={onSource}><CaseReviewIcon name="file" size={15} />Show in source</button>}
      <span className="cr-toolbar-spacer" />
      {canDecide && canAccept && isReviewerChange(decision) && <button type="button" className="cr-popover-restore" onClick={() => { onRestoreAiValue(); onClose(); }}><CaseReviewIcon name="undo" size={13} strokeWidth={2.2} />{RESTORE_AI_VALUE_LABEL}</button>}
      {canDecide && !decision && <button type="button" className="cr-popover-accept" disabled={!canAccept}
        title={canAccept ? undefined : MANUAL_ENTRY_ACCEPT_TITLE} onClick={() => { onAccept(); onClose(); }}><CaseReviewIcon name="check" size={13} strokeWidth={2.6} />Accept</button>}
      {canDecide && !decision && <button type="button" className="cr-popover-reject" title={REJECT_AI_VALUE_TITLE} onClick={() => { onReject(); onClose(); }}><CaseReviewIcon name="x" size={12} strokeWidth={2.2} />Reject</button>}
    </div>
  </div>;
});

interface DisclosureProps extends Omit<PopoverProps, "onClose"> {
  open: boolean;
  onOpenChange(open: boolean): void;
}

/** One accessible disclosure per AI field; Radix owns dismissal and closes the prior field when another opens. */
export function AiReasoningDisclosure({ open, onOpenChange, ...popover }: DisclosureProps) {
  return <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
    <PopoverPrimitive.Trigger asChild>
      <ConfidencePill evidence={popover.evidence} decision={popover.decision} open={open} showConfidence={popover.showConfidence} />
    </PopoverPrimitive.Trigger>
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content asChild side="bottom" align="end" sideOffset={6} collisionPadding={8}
        onOpenAutoFocus={(event) => event.preventDefault()}>
        <ReasoningPopover {...popover} onClose={() => onOpenChange(false)} />
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  </PopoverPrimitive.Root>;
}

interface ProposalDisclosureProps {
  label: string;
  proposal: FieldProposal;
  open: boolean;
  onOpenChange(open: boolean): void;
}

/**
 * The Theragenie pill on a proposed row, and the rationale behind it with the same ✓/✕ the row
 * offers. Its own disclosure rather than a branch of the AI one: a proposal is not evidence, and
 * a field can carry both.
 */
export function ProposalDisclosure({ label, proposal, open, onOpenChange }: ProposalDisclosureProps) {
  const close = () => onOpenChange(false);
  return <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
    <PopoverPrimitive.Trigger asChild>
      <button type="button" className="cr-pill cr-proposal-pill" data-open={open} title={PROPOSAL_PILL_TITLE} aria-expanded={open} aria-haspopup="dialog">
        {THERAGENIE_LABEL}<CaseReviewIcon name="spark" size={11} strokeWidth={2.2} />
      </button>
    </PopoverPrimitive.Trigger>
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content asChild side="bottom" align="end" sideOffset={6} collisionPadding={8}
        onOpenAutoFocus={(event) => event.preventDefault()}>
        <div className="cr-portal cr-popover" role="dialog" aria-label={`${THERAGENIE_LABEL} proposal for ${label}`}
          onKeyDown={(event) => { if (event.key === "Escape") { event.stopPropagation(); close(); } }}
          onClick={(event) => event.stopPropagation()}>
          <div className="cr-popover-head">
            <span className="cr-popover-spark"><CaseReviewIcon name="spark" size={15} /></span>
            <div className="cr-popover-title">
              <div className="cr-popover-label">{label}</div>
              <div className="cr-popover-meta"><span className="cr-pill cr-pill-static cr-proposal-pill">{THERAGENIE_LABEL} proposal</span></div>
            </div>
            <button type="button" className="cr-popover-close" aria-label="Close" onClick={close}><CaseReviewIcon name="x" size={12} strokeWidth={2.2} /></button>
          </div>
          <p className="cr-popover-text">{proposal.proposal.rationale}</p>
          <div className="cr-popover-actions">
            <span className="cr-toolbar-spacer" />
            <button type="button" className="cr-popover-accept" onClick={() => { proposal.accept(); close(); }}><CaseReviewIcon name="check" size={13} strokeWidth={2.6} />Accept</button>
            <button type="button" className="cr-popover-reject" title={PROPOSAL_REJECT_TITLE} onClick={() => { proposal.reject(); close(); }}><CaseReviewIcon name="x" size={12} strokeWidth={2.2} />Reject</button>
          </div>
        </div>
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  </PopoverPrimitive.Root>;
}

interface ActionsProps {
  label: string; hasReview: boolean; decision?: ReviewDecision; disabled?: boolean;
  /** The extraction could not be used: Accept is off and there is nothing to restore. */
  acceptDisabled?: boolean;
  /** Theragenie's pending proposal on this field: its ✓/✕ take the row's place while it waits. */
  proposal?: FieldProposal;
  onAccept: () => void; onReject: () => void; onRestoreAiValue: () => void; onRevert: () => void;
}

/** Pending AI values show review actions; an edited or rejected AI field instead offers an explicit restoration. */
export function AiFieldActions({ label, hasReview, decision, disabled, acceptDisabled, proposal, onAccept, onReject, onRestoreAiValue, onRevert }: ActionsProps) {
  if (proposal) {
    return <>
      <button type="button" className="cr-action cr-action-accept cr-proposal-action" disabled={disabled}
        aria-label={`Accept ${label}`} title={PROPOSAL_ACCEPT_TITLE} onClick={(event) => { event.stopPropagation(); proposal.accept(); }}>
        <CaseReviewIcon name="check" size={13} strokeWidth={2.6} />
      </button>
      <button type="button" className="cr-action cr-action-reject cr-proposal-action" disabled={disabled}
        aria-label={`Reject ${label}`} title={PROPOSAL_REJECT_TITLE} onClick={(event) => { event.stopPropagation(); proposal.reject(); }}><CaseReviewIcon name="x" size={12} strokeWidth={2.2} /></button>
    </>;
  }
  if (!hasReview && !decision) return null;
  const accepted = decision === REVIEW_DECISIONS.accepted;
  if (isReviewerChange(decision)) {
    // No usable extraction means no AI value to restore: the reviewer's value stays.
    if (acceptDisabled) return null;
    return <button type="button" className="cr-action-restore" disabled={disabled}
      aria-label={`${RESTORE_AI_VALUE_LABEL} for ${label}`} title="Restore the original extracted AI value"
      onClick={(event) => { event.stopPropagation(); onRestoreAiValue(); }}>
      <CaseReviewIcon name="undo" size={12} strokeWidth={2.2} />{RESTORE_AI_VALUE_LABEL}
    </button>;
  }
  return <>
    <button type="button" className="cr-action cr-action-accept" data-on={accepted} disabled={disabled || (acceptDisabled && !accepted)}
      aria-pressed={accepted} aria-label={accepted ? `Undo acceptance of ${label}` : `Accept ${label}`}
      title={accepted ? "Accepted — click to undo" : acceptDisabled ? MANUAL_ENTRY_ACCEPT_TITLE : "Accept AI value"} onClick={(event) => { event.stopPropagation(); if (accepted) onRevert(); else onAccept(); }}>
      <CaseReviewIcon name="check" size={13} strokeWidth={2.6} />
    </button>
    <button type="button" className="cr-action cr-action-reject" disabled={disabled} data-dim={accepted}
      aria-label={`Reject ${label}`} title={REJECT_AI_VALUE_TITLE} onClick={(event) => { event.stopPropagation(); onReject(); }}><CaseReviewIcon name="x" size={12} strokeWidth={2.2} /></button>
  </>;
}
